/**
 * Compra com cartão em andamento, guardada para sobreviver a um F5.
 *
 * Depois do OTP a compra vale 30 minutos e o `hash_unico` é a única chave
 * para retomá-la. O wizard vive só em memória, então o hash, o carrinho e os
 * detalhes do plano que o carrinho não guarda ficam no `sessionStorage` (por
 * aba, some ao fechá-la) até a compra ser paga, encerrada ou abandonada.
 */
export interface PlanoPersistido {
  planIndex: number
  chosenPlan: string
  pickUpChip: boolean
  smartControl: boolean
  portability: boolean
  chipOnly: boolean
  chipQuantity: number
}

export interface CupomPersistido {
  texto: string
  valor: number
  percentual: boolean
}

export interface CheckoutPersistido {
  hash: string
  cartId: string
  virtualChip: boolean
  plano: PlanoPersistido
  cupom: CupomPersistido | null
}

const CHAVE = 'checkoutCartao'
const HASH_UNICO = /^[0-9a-f]{64}$/i

function objeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null
}

function planoValido(plano: unknown): plano is PlanoPersistido {
  if (!objeto(plano)) return false
  const { planIndex, chosenPlan, pickUpChip, smartControl, portability, chipOnly, chipQuantity } = plano

  return typeof planIndex === 'number'
    && typeof chosenPlan === 'string'
    && typeof pickUpChip === 'boolean'
    && typeof smartControl === 'boolean'
    && typeof portability === 'boolean'
    && typeof chipOnly === 'boolean'
    && typeof chipQuantity === 'number'
}

function cupomValido(cupom: unknown): cupom is CupomPersistido | null {
  if (cupom === null) return true
  if (!objeto(cupom)) return false
  const { texto, valor, percentual } = cupom

  return typeof texto === 'string' && typeof valor === 'number' && typeof percentual === 'boolean'
}

function valido(dados: unknown): dados is CheckoutPersistido {
  if (!objeto(dados)) return false
  const { hash, cartId, virtualChip, plano, cupom } = dados

  return typeof hash === 'string' && HASH_UNICO.test(hash)
    && typeof cartId === 'string'
    && typeof virtualChip === 'boolean'
    && planoValido(plano)
    && cupomValido(cupom)
}

export function salvarCheckout(checkout: CheckoutPersistido): void {
  try {
    sessionStorage.setItem(CHAVE, JSON.stringify(checkout))
  } catch {
    // Storage indisponível (aba anônima, cota): a compra só não sobrevive ao F5.
  }
}

export function limparCheckout(): void {
  try {
    sessionStorage.removeItem(CHAVE)
  } catch {
    // Storage indisponível: não há o que limpar.
  }
}

/** Devolve a compra guardada, ou `null`; um registro inválido é descartado. */
export function lerCheckout(): CheckoutPersistido | null {
  let bruto: string | null
  try {
    bruto = sessionStorage.getItem(CHAVE)
  } catch {
    return null
  }
  if (bruto === null) return null

  try {
    const dados: unknown = JSON.parse(bruto)
    if (valido(dados)) {
      const { hash, cartId, virtualChip, plano, cupom } = dados
      const { planIndex, chosenPlan, pickUpChip, smartControl, portability, chipOnly, chipQuantity } = plano

      return {
        hash,
        cartId,
        virtualChip,
        plano: { planIndex, chosenPlan, pickUpChip, smartControl, portability, chipOnly, chipQuantity },
        cupom: cupom && { texto: cupom.texto, valor: cupom.valor, percentual: cupom.percentual },
      }
    }
  } catch {
    // JSON corrompido: cai no descarte abaixo.
  }

  limparCheckout()
  return null
}
